import { FormEvent, useEffect, useState } from 'react';

type Settings = {
  botToken: string;
  chatId: string;
  chatTitle: string;
  sendTime: string;
  configured: boolean;
};

type Chat = { id: string; title: string; type: string };
type Report = { kind: string; text: string };

async function readError(res: Response) {
  if (res.status === 502 || res.status === 504) return '报表计算超时，请再点一次预览';
  const data = await res.json().catch(() => ({}));
  return data.message || '请求失败';
}

const REPORT_KINDS = ['daily', 'weekly', 'monthly'] as const;

export default function TelegramCard({ token }: { token: string }) {
  const [botToken, setBotToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [chatTitle, setChatTitle] = useState('');
  const [sendTime, setSendTime] = useState('08:00');
  const [chats, setChats] = useState<Chat[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetch(`/api/telegram?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(await readError(res));
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const settings = data.settings as Settings;
        setBotToken(settings.botToken || '');
        setChatId(settings.chatId || '');
        setChatTitle(settings.chatTitle || '');
        setSendTime(settings.sendTime || '08:00');
        setReady(true);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : '读取设置失败');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const payload = () => ({ token, botToken: botToken.trim(), chatId: chatId.trim(), chatTitle, sendTime });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy('save');
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/telegram', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload()),
      });
      if (!res.ok) throw new Error(await readError(res));
      const data = await res.json();
      setChatTitle(data.settings.chatTitle || '');
      setSendTime(data.settings.sendTime || '08:00');
      setMessage('已保存。到了发送时间会自动推送；周一加发周报，每月 1 日加发月报。');
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败');
    } finally {
      setBusy('');
    }
  };

  const loadChats = async () => {
    setBusy('chats');
    setError('');
    setMessage('');
    setChats([]);
    try {
      const res = await fetch('/api/telegram/chats', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload()),
      });
      if (!res.ok) throw new Error(await readError(res));
      const data = await res.json();
      const list = (data.chats || []) as Chat[];
      setChats(list);
      if (!list.length) {
        setError('还没有读到群。请把机器人拉进群，在群里发一句消息后再获取。');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取群组失败');
    } finally {
      setBusy('');
    }
  };

  const preview = async () => {
    setBusy('preview');
    setError('');
    setMessage('');
    setReports([]);
    const next: Report[] = [];
    try {
      for (const kind of REPORT_KINDS) {
        const res = await fetch('/api/telegram/preview', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, kind }),
        });
        if (!res.ok) throw new Error(await readError(res));
        const data = await res.json();
        next.push(...(data.reports || []));
        setReports([...next]);
      }
    } catch (err) {
      if (next.length) setMessage('已生成部分报表，其余计算超时，请再点一次预览。');
      setError(err instanceof Error ? err.message : '预览失败');
    } finally {
      setBusy('');
    }
  };

  const sendKinds = async (kinds: readonly string[], busyKey: string, success: string) => {
    setBusy(busyKey);
    setError('');
    setMessage('');
    let sent = 0;
    try {
      for (const kind of kinds) {
        const res = await fetch('/api/telegram/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload(), kind }),
        });
        if (!res.ok) throw new Error(await readError(res));
        sent += 1;
      }
      setMessage(success);
    } catch (err) {
      if (sent) setMessage(`已发出 ${sent} 份，其余发送失败。`);
      setError(err instanceof Error ? err.message : '发送失败');
    } finally {
      setBusy('');
    }
  };

  const sendDaily = () => sendKinds(['daily'], 'daily', '昨天的日报已发到群里。这是手动发送，不会占用到点自动推送。');
  const send = () => sendKinds(REPORT_KINDS, 'send', '三份报表已发到群里。这是测试发送，不会占用到点自动推送。');

  return (
    <section className="mt-4 rounded-lg border border-background-200 bg-background-50 p-6">
      <h2 className="text-sm font-semibold text-foreground-900">Telegram 报表</h2>
      <p className="mt-2 text-sm leading-6 text-foreground-600">
        每天在设定时间发送昨天的日报。如果这天是周一，接着发上周周报；如果是每月 1 日，接着发上月月报。Token 和群组填一次即可，平时只改发送时间。
      </p>
      <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm leading-6 text-foreground-600">
        <li>在 Telegram 打开 @BotFather，发送 /newbot，把 Token 粘贴到下面。</li>
        <li>把机器人拉进统计群，在群里发一句消息。</li>
        <li>点「获取群组」，选中这个群。</li>
        <li>设置发送时间并保存。需要立刻看效果时，点「发送昨天日报到群」或「发送三份到群里」。</li>
      </ol>

      {loading ? (
        <p className="mt-4 text-sm text-foreground-500">正在读取设置…</p>
      ) : !ready ? null : (
        <form onSubmit={save} className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm text-foreground-700">
            机器人 Token
            <input
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
              placeholder="123456:ABC..."
              autoComplete="off"
              className="px-4 py-2.5 rounded-md border border-background-200 text-sm"
            />
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <label className="flex flex-1 flex-col gap-1 text-sm text-foreground-700">
              群组 Chat ID
              <input
                value={chatId}
                onChange={(e) => setChatId(e.target.value)}
                placeholder="-1001234567890"
                className="px-4 py-2.5 rounded-md border border-background-200 text-sm"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-foreground-700 sm:w-36">
              发送时间
              <input
                type="time"
                required
                value={sendTime}
                onChange={(e) => setSendTime(e.target.value)}
                className="px-4 py-2.5 rounded-md border border-background-200 text-sm"
              />
            </label>
          </div>
          {chatTitle && <p className="text-sm text-foreground-600">当前群组：{chatTitle}</p>}
          {chats.length > 0 && (
            <div className="flex flex-col gap-2">
              {chats.map((chat) => (
                <button
                  key={chat.id}
                  type="button"
                  onClick={() => {
                    setChatId(chat.id);
                    setChatTitle(chat.title);
                  }}
                  className={`rounded-md border px-3 py-2 text-left text-sm ${
                    chatId === chat.id ? 'border-accent-500 bg-accent-500/10' : 'border-background-200'
                  }`}
                >
                  {chat.title}
                  <span className="ml-2 text-foreground-500">{chat.id}</span>
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <button type="submit" disabled={busy !== ''} className="px-4 py-2.5 text-sm font-semibold text-background-50 bg-accent-500 rounded-md disabled:opacity-60">
              {busy === 'save' ? '保存中…' : '保存设置'}
            </button>
            <button type="button" disabled={busy !== ''} onClick={loadChats} className="px-4 py-2.5 text-sm font-semibold rounded-md border border-background-200 disabled:opacity-60">
              {busy === 'chats' ? '获取中…' : '获取群组'}
            </button>
            <button type="button" disabled={busy !== ''} onClick={preview} className="px-4 py-2.5 text-sm font-semibold rounded-md border border-background-200 disabled:opacity-60">
              {busy === 'preview' ? '生成中…' : '预览三份报表'}
            </button>
            <button type="button" disabled={busy !== ''} onClick={sendDaily} className="px-4 py-2.5 text-sm font-semibold rounded-md border border-background-200 disabled:opacity-60">
              {busy === 'daily' ? '发送中…' : '发送昨天日报到群'}
            </button>
            <button type="button" disabled={busy !== ''} onClick={send} className="px-4 py-2.5 text-sm font-semibold rounded-md border border-background-200 disabled:opacity-60">
              {busy === 'send' ? '发送中…' : '发送三份到群里'}
            </button>
          </div>
        </form>
      )}

      {reports.length > 0 && (
        <div className="mt-4 flex flex-col gap-3">
          {reports.map((report) => (
            <pre key={report.kind} className="overflow-auto whitespace-pre-wrap rounded-md bg-background-100 p-4 text-xs leading-5 text-foreground-900">
              {report.text}
            </pre>
          ))}
        </div>
      )}
      {message && <p className="mt-4 text-sm text-accent-700">{message}</p>}
      {error && <p className="mt-4 text-sm text-primary-700">{error}</p>}
      <p className="mt-4 text-xs leading-5 text-foreground-500">
        Docker 或本机启动后会按时间自动发送。宝塔请加一条每分钟执行的计划任务：php 网站目录/api/cron-telegram.php
      </p>
    </section>
  );
}
